import { useCallback, useEffect, useState } from 'react';
import {
  DEPOSIT_GRADE_INFO, HUB_KIND_LABEL, LOGISTICS, STAR_TYPE_MODIFIERS, formatGameDuration, formatLy, type EconomyOverview, type EconomySource,
} from '@galaxis/shared';
import { fetchEconomyOverview, upgradeStation, fetchResources } from '../api/galaxy.ts';
import { useGameStore } from '../store/game-store.ts';
import { GuideHint } from './guide/GuideHint.tsx';
import { postOnboarding } from '../api/onboarding.ts';
import { perMinute } from '../economy/live-resources.ts';
import { useLiveResources } from '../economy/live-store.ts';

const REASON_LABEL: Record<string, string> = {
  'build:outpost': 'Előőrs építése',
  'build:mining_station': 'Bányaállomás építése',
  'build:energy_station': 'Energiaállomás építése',
  'build:corvette': 'Korvett gyártása',
  'build:frigate': 'Fregatt gyártása',
  'build:cruiser': 'Cirkáló gyártása',
  'build:construction': 'Építőhajó gyártása',
  'build:scout': 'Felderítő gyártása',
  'build:logistics_hub': 'Elosztóközpont építése',
  spend: 'Kiadás',
  grant: 'Jóváírás',
};

export function reasonLabel(reason: string): string {
  if (REASON_LABEL[reason]) return REASON_LABEL[reason]!;
  const up = /^upgrade:(\w+):L(\d)$/.exec(reason);
  if (up) return `${up[1] === 'energy_station' ? 'Energiaállomás' : 'Bányaállomás'} fejlesztése → ${up[2]}. szint`;
  if (reason.startsWith('research:')) return `Kutatás: ${reason.slice(9)}`;
  if (reason.startsWith('trade:')) return 'Kereskedelem';
  return reason;
}

function fmtDuration(ms: number): string {
  if (ms <= 0) return 'most';
  const m = Math.floor(ms / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  if (m >= 60) return `${Math.floor(m / 60)} ó ${m % 60} p`;
  return m > 0 ? `${m} p ${s} mp` : `${s} mp`;
}

const KIND_LABEL: Record<EconomySource['kind'], string> = {
  home: 'Anyarendszer',
  mining_station: 'Bányaállomás',
  energy_station: 'Energiaállomás',
  trade: 'Kereskedelem',
  logistics_hub: 'Elosztóközpont',
};

const STAR_LABEL: Record<string, string> = {
  yellow_dwarf: 'sárga törpe', red_dwarf: 'vörös törpe', blue_giant: 'kék óriás', white_dwarf: 'fehér törpe',
  neutron_star: 'neutroncsillag', black_hole: 'fekete lyuk', binary: 'kettőscsillag',
};

/** Track 7: why a station delivers what it delivers — grade, star, tier and the transport to the nearest hub. */
function SourceDetails({ src }: { src: EconomySource }) {
  if (src.kind !== 'mining_station' && src.kind !== 'energy_station') return null;
  const t = src.transport;
  const lossy = !!t && t.efficiency < 0.995;
  const star = src.starType ? STAR_TYPE_MODIFIERS[src.starType as keyof typeof STAR_TYPE_MODIFIERS] : undefined;
  return (
    <div className="mt-1 space-y-0.5 text-[11px] text-slate-500" data-testid={`source-details-${src.id}`}>
      <div>
        {src.tierName ?? `L${src.level}`}
        {src.grade && <> · lelőhely: <span className="text-slate-300" data-testid="source-grade">{DEPOSIT_GRADE_INFO[src.grade].label}</span> (×{DEPOSIT_GRADE_INFO[src.grade].multiplier})</>}
        {src.starType && <> · {STAR_LABEL[src.starType] ?? src.starType}{star ? ` (${src.kind === 'mining_station' ? 'fém' : 'energia'} ×${src.kind === 'mining_station' ? star.metal : star.energy})` : ''}</>}
      </div>
      {t && (
        <div className={lossy ? 'text-amber-300/90' : 'text-emerald-400/80'} data-testid="source-transport">
          Szállítás {Math.round(t.efficiency * 100)}%
          {t.hubSystemName
            ? <> · {t.distanceLy >= 0 ? formatLy(t.distanceLy) : '—'} a legközelebbi csomóponttól ({t.hubSystemName}, {HUB_KIND_LABEL[t.hubKind!]})</>
            : ' · nincs elérhető csomópont'}
          {lossy && <> · veszteség {perMinute(-t.lostPerHour)}/p</>}
          {lossy && <span className="block text-slate-500">{formatLy(LOGISTICS.freeRadiusLy)}-ig veszteségmentes, fölötte {(LOGISTICS.lossPerLy * 100).toFixed(1).replace('.', ',')}%/ly. Elosztóközpont a környéken helyreállítja.</span>}
        </div>
      )}
    </div>
  );
}

function SourceRow({ src, now, canAfford, onUpgrade, busy }: {
  src: EconomySource; now: number; canAfford: boolean; onUpgrade: () => void; busy: boolean;
}) {
  const isStation = src.kind === 'mining_station' || src.kind === 'energy_station';
  const isHub = src.kind === 'logistics_hub';
  const upgrading = src.upgradingTo !== null && src.upgradeCompletesAt;
  const remaining = upgrading ? new Date(src.upgradeCompletesAt!).getTime() - now : 0;
  return (
    <li className="sci-fi-panel rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`source-${src.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-slate-200 font-medium truncate">{src.systemName}</span>
          <span className="text-[10px] uppercase tracking-wider text-slate-500">{KIND_LABEL[src.kind]}</span>
          {isStation && <span className="text-xs font-mono text-accent">L{src.level}</span>}
          {src.status !== 'active' && <span className="text-xs text-red-400">{src.status === 'damaged' ? 'sérült' : src.status}</span>}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          {src.depositName ? `${src.depositName} · ` : ''}
          <span className="font-mono text-slate-300">{perMinute(src.effectivePerHour)}/p</span>
          {src.resource === 'both' ? ' fém és energia' : src.resource === 'metal' ? ' fém' : ' energia'}
          {src.upkeepPerHour > 0 && !isHub && <span className="text-amber-400/80"> · fenntartás {perMinute(-src.upkeepPerHour)}/p energia</span>}
          {isHub && <span className="text-slate-400">a környező állomások szállítási veszteségét szünteti meg · fenntartás {perMinute(-src.upkeepPerHour)}/p fém</span>}
        </div>
        <SourceDetails src={src} />
      </div>
      {upgrading ? (
        <div className="text-xs text-accent font-mono" role="status">
          Fejlesztés → L{src.upgradingTo}: {fmtDuration(remaining)}
        </div>
      ) : src.upgrade ? (
        <button
          type="button"
          className="btn-primary text-xs min-h-[44px] px-3 disabled:opacity-40 sm:w-auto sm:shrink-0"
          disabled={!canAfford || busy}
          onClick={onUpgrade}
          title={canAfford ? undefined : 'Nincs elég nyersanyag'}
        >
          Fejlesztés L{src.upgrade.toLevel} ({src.upgrade.metal} fém, {src.upgrade.energy} energia)
        </button>
      ) : isStation ? (
        <span className="text-xs text-slate-500">Max szint</span>
      ) : null}
    </li>
  );
}

export function EconomyView() {
  const [ov, setOv] = useState<EconomyOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const resources = useGameStore(s => s.resources);
  const { setResources, pushToast, setOnboarding } = useGameStore();
  const nextStep = useGameStore(s => s.onboarding?.next);
  const live = useLiveResources();

  const load = useCallback(() => {
    fetchEconomyOverview().then(o => { setOv(o); setError(null); }).catch(e => setError(e instanceof Error ? e.message : 'Hiba'));
  }, []);

  // Reload whenever the server pushes new resource numbers (build, upgrade, battle…)
  useEffect(() => { load(); }, [load, resources]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Looking at a station's production is the "production" step
  const hasStation = !!ov && ov.sources.some(s => s.kind === 'mining_station' || s.kind === 'energy_station');
  useEffect(() => {
    if (nextStep === 'productionSeen' && hasStation) void postOnboarding('production_seen').then(setOnboarding).catch(() => undefined);
  }, [nextStep, hasStation, setOnboarding]);

  const upgrade = async (src: EconomySource) => {
    setBusy(src.id);
    try {
      await upgradeStation(src.id);
      pushToast(`${src.systemName}: fejlesztés elindítva`);
      setResources(await fetchResources());
      load();
    } catch (e) {
      pushToast(e instanceof Error ? e.message : 'A fejlesztés nem sikerült', 'error');
    } finally {
      setBusy(null);
    }
  };

  if (error) return <div className="flex-1 p-6 text-red-400 text-sm">{error}</div>;
  if (!ov || !live) return <div className="flex-1 p-6 text-slate-500 text-sm">Gazdaság betöltése…</div>;

  const deficit = ov.energy.netPerHour < 0;
  const starving = ov.efficiency < 1;
  const metalUpkeep = ov.metal.upkeepPerHour ?? 0;
  const metalLoss = ov.metal.grossPerHour - metalUpkeep < 0;
  const metalFull = !!ov.storage && ov.resources.metal >= ov.storage.metal && ov.metal.netPerHour > 0;
  const energyFull = !!ov.storage && ov.resources.energy >= ov.storage.energy && ov.energy.netPerHour > 0;

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6" data-testid="economy-view">
      <div className="max-w-5xl mx-auto space-y-4">
        <h1 className="text-lg font-semibold text-slate-200">Gazdaság</h1>
        <GuideHint steps={['productionSeen']} />
        <GuideHint steps={['energySeen']}
          text={deficit ? 'Most negatív az energiamérleged — pont ez az, amit érdemes megérteni: építs energiaállomást, mielőtt a bányák lelassulnak.' : undefined}
          action={{ label: 'Értem az energiamérleget', onClick: () => { void postOnboarding('energy_seen').then(setOnboarding).catch(() => undefined); } }} />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sci-fi-panel rounded-xl p-4" data-testid="metal-card">
            <div className="text-xs text-slate-500 uppercase tracking-wider">⚙️ Fém</div>
            <div className="text-2xl font-mono text-slate-100">{live.metal.toLocaleString('en-US')}</div>
            <div className="text-xs text-slate-400 mt-1 font-mono">
              bruttó {perMinute(ov.metal.grossPerHour)}/p · fenntartás {perMinute(-metalUpkeep)}/p ·
              nettó <span className={metalLoss ? 'text-red-400' : 'text-emerald-400'}>{perMinute(metalLoss ? ov.metal.grossPerHour - metalUpkeep : ov.metal.netPerHour)}/p</span>
            </div>
            {ov.storage && <div className="text-xs text-slate-500 font-mono" data-testid="metal-storage">raktár {live.metal.toLocaleString('en-US')} / {ov.storage.metal.toLocaleString('en-US')}</div>}
          </div>
          <div className={`sci-fi-panel rounded-xl p-4 ${deficit ? 'ring-1 ring-red-500/50' : ''}`} data-testid="energy-card">
            <div className="text-xs text-slate-500 uppercase tracking-wider">⚡ Energia</div>
            <div className="text-2xl font-mono text-slate-100">{live.energy.toLocaleString('en-US')}</div>
            <div className="text-xs text-slate-400 mt-1 font-mono">
              termelés {perMinute(ov.energy.grossPerHour)}/p · fenntartás {perMinute(-ov.energy.upkeepPerHour)}/p ·
              nettó <span className={deficit ? 'text-red-400' : 'text-emerald-400'}>{perMinute(ov.energy.netPerHour)}/p</span>
            </div>
            {ov.storage && <div className="text-xs text-slate-500 font-mono" data-testid="energy-storage">raktár {live.energy.toLocaleString('en-US')} / {ov.storage.energy.toLocaleString('en-US')}</div>}
          </div>
        </div>

        {(deficit || starving) && (
          <div role="alert" className="rounded-xl p-3 text-sm border border-red-500/40 bg-red-500/10 text-red-300">
            {starving
              ? `Energiahiány! A bányák ${Math.round(ov.efficiency * 100)}%-os hatékonysággal működnek. Építs energiaállomást, vagy csökkentsd a flották/állomások fenntartását.`
              : `Negatív energiamérleg. A tartalék ${ov.energyEmptyAt ? fmtDuration(new Date(ov.energyEmptyAt).getTime() - now) + ' múlva' : 'hamarosan'} elfogy, utána a bányák lassabban termelnek.`}
          </div>
        )}

        {metalLoss && (
          <div role="alert" data-testid="metal-upkeep-alert" className="rounded-xl p-3 text-sm border border-amber-500/40 bg-amber-500/10 text-amber-300">
            A fém fenntartási költség meghaladja a bevételt: nincs új megtakarítás
            {ov.metalEmptyAt ? `, a tartalék ${fmtDuration(new Date(ov.metalEmptyAt).getTime() - now)} múlva elfogy` : ''}.
            Építs bányát, fejlessz állomást, vagy csökkentsd a flottát és a terjeszkedést.
          </div>
        )}
        {(metalFull || energyFull) && (
          <div role="status" data-testid="storage-full" className="rounded-xl p-3 text-sm border border-sky-500/40 bg-sky-500/10 text-sky-300">
            A raktár megtelt, a többlettermelés elvész. Költs a készletből: fejlesztés, kutatás, hajók vagy új előőrs.
          </div>
        )}

        {ov.logistics && (
          <section className="sci-fi-panel rounded-xl p-4" data-testid="logistics-card">
            <h2 className="text-sm font-semibold text-slate-300 mb-2">Logisztika és építők</h2>
            <dl className="text-xs space-y-1">
              <div className="flex justify-between gap-2"><dt className="text-slate-400">Átlagos szállítási hatékonyság</dt>
                <dd className="font-mono" data-testid="avg-efficiency">{Math.round(ov.logistics.averageEfficiency * 100)}%</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-slate-400">Távolság miatt elvesztett termelés</dt>
                <dd className="font-mono">{perMinute(-ov.logistics.lostMetalPerHour)}/p fém · {perMinute(-ov.logistics.lostEnergyPerHour)}/p energia</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-slate-400">Csomópontok</dt>
                <dd className="font-mono text-right">{ov.logistics.hubs.map(h => `${h.systemName} (${HUB_KIND_LABEL[h.kind]})`).join(', ') || '—'}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-slate-400">Elosztóközpontok</dt>
                <dd className="font-mono">{ov.logistics.hubCount} / {ov.logistics.maxHubs} · {ov.logistics.hubCost.metal} fém, {ov.logistics.hubCost.energy} energia, {formatGameDuration(ov.logistics.hubCost.timeMs)}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-slate-400">Építőhajók</dt>
                <dd className="font-mono">{ov.logistics.builders} / {ov.logistics.maxBuilders}</dd></div>
            </dl>
            <p className="text-[11px] text-slate-500 mt-2">
              A bányák és erőművek termelése a legközelebbi csomóponttól (főváros, elosztóközpont vagy működő kereskedelmi útvonal vége) mért távolságtól függ:
              {' '}{formatLy(LOGISTICS.freeRadiusLy)}-ig teljes, fölötte ly-onként {(LOGISTICS.lossPerLy * 100).toFixed(1).replace('.', ',')}%-ot veszít, legalább {Math.round(LOGISTICS.minEfficiency * 100)}% marad.
            </p>
          </section>
        )}

        <section>
          <h2 className="text-sm font-semibold text-slate-300 mb-2">Termelőforrások</h2>
          <ul className="space-y-2">
            {ov.sources.map(src => (
              <SourceRow
                key={src.id}
                src={src}
                now={now}
                busy={busy === src.id}
                canAfford={!!src.upgrade && live.metal >= src.upgrade.metal && live.energy >= src.upgrade.energy}
                onUpgrade={() => void upgrade(src)}
              />
            ))}
            {ov.sources.filter(x => x.kind === 'mining_station' || x.kind === 'energy_station').length === 0 && (
              <li className="text-xs text-slate-500 p-2">Építs bánya- vagy energiaállomást a felmért rendszerek lelőhelyein (Galaxis → rendszer → Építés).</li>
            )}
          </ul>
        </section>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <section className="sci-fi-panel rounded-xl p-4">
            <h2 className="text-sm font-semibold text-slate-300 mb-2">Fenntartás (energia · fém)</h2>
            <dl className="text-xs font-mono space-y-1" data-testid="upkeep-list">
              <div className="flex justify-between"><dt className="text-slate-400">Állomások</dt><dd>{perMinute(-ov.upkeep.stations)} · {perMinute(-(ov.metalUpkeep?.stations ?? 0))}/p</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Előőrsök</dt><dd>{perMinute(-ov.upkeep.outposts)} · {perMinute(-(ov.metalUpkeep?.outposts ?? 0))}/p</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Flották</dt><dd>{perMinute(-ov.upkeep.fleets)} · {perMinute(-(ov.metalUpkeep?.fleets ?? 0))}/p</dd></div>
            </dl>
          </section>
          <section className="sci-fi-panel rounded-xl p-4">
            <h2 className="text-sm font-semibold text-slate-300 mb-2">Legutóbbi tranzakciók</h2>
            {ov.ledger.length === 0 ? (
              <p className="text-xs text-slate-500">Még nincs költés.</p>
            ) : (
              <ul className="text-xs space-y-1" data-testid="ledger">
                {ov.ledger.map((l, i) => (
                  <li key={i} className="flex justify-between gap-2">
                    <span className="text-slate-400 truncate">{reasonLabel(l.reason)}</span>
                    <span className="font-mono text-slate-300 whitespace-nowrap">
                      {l.metal !== 0 && `${l.metal > 0 ? '+' : ''}${l.metal} fém `}
                      {l.energy !== 0 && `${l.energy > 0 ? '+' : ''}${l.energy} en.`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
